import { MapUtils } from '../../../utility/map-utils.js';
import { isSameData } from '../../dto/data-model-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { VersionRange } from '../../core/version-range.js';
import { LocalizedString } from '../localized-string.js';
import { Price } from '../price.js';
import { PassVariationChannel } from './open-access-pass-variation.js';
import { toEnumFromString } from '../../../serialization/enum-utils.js';

export class OpenAccessPassVariationDetails extends DataModel {
  static readonly TYPE_ID: string = '1a732f1abe754f85a0a3d492fee364f8';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly uniqueId: string,
    public readonly names: LocalizedString,
    public readonly price: Price,
    public readonly channel: PassVariationChannel,
  ) {
    super();
  }

  get $typeId(): string {
    return OpenAccessPassVariationDetails.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, OpenAccessPassVariationDetails.SCHEMA_VERSION);
  }

  static create(opts: {
    uniqueId: string,
    names: LocalizedString,
    price: Price,
    channel: PassVariationChannel,
  }): OpenAccessPassVariationDetails {
    return new OpenAccessPassVariationDetails(
        opts.uniqueId,
        opts.names,
        opts.price,
        opts.channel,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassVariationDetails {
    return new OpenAccessPassVariationDetails(
        MapUtils.getString(map, 'uniqueId'),
        context.fromData<LocalizedString>(MapUtils.getRecord(map, 'names')),
        Price.decode(MapUtils.getString(map, 'price')),
        toEnumFromString(MapUtils.getStringOrNull(map, 'channel'), PassVariationChannel),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'names': context.toData(this.names),
      'price': this.price.encode(),
      'channel': this.channel,
    };
  }

  clone(): this {
    return new OpenAccessPassVariationDetails(
        this.uniqueId,
        this.names.clone(),
        this.price.clone(),
        this.channel,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.names, this.names) &&
        isSameData(other.price, this.price) &&
        other.uniqueId === this.uniqueId &&
        other.channel === this.channel;
  }
}

export class OpenAccessPassVariationDetailsBuilder extends DataModelBuilder<OpenAccessPassVariationDetails> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassVariationDetails {
    return OpenAccessPassVariationDetails.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(OpenAccessPassVariationDetails.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OpenAccessPassVariationDetails.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [channel] field of type [PassVariationChannel] was introduced.
    if(version < 2) {
      map['channel'] ??= PassVariationChannel.Admin;
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
